import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { AssetRecord } from "@/types/assets";
import { Badge } from "@/components/ui/badge";
import { AppleCard } from "@/components/ui/apple-card";
import { cn, getStatusColor, getPriorityColor } from "@/lib/utils";
import { ChevronDown, Calendar, Clock, AlertCircle, DollarSign, MapPin, User, Phone, Image as ImageIcon, FileText, X, Plus, Search, ChevronLeft, ChevronRight, Pencil } from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { AppleButton } from "@/components/ui/apple-button";
import { AssetRecordForm } from "@/components/forms/AssetRecordForm";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { CreateReminderForm } from "@/components/forms/CreateReminderForm"
import { Bell } from "lucide-react"
import { useQuery } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import { differenceInDays, isToday } from "date-fns"
import { toast } from "@/components/ui/use-toast"
import { AssetReminders } from "@/components/assets/AssetReminders";
import { Database } from "@/integrations/supabase/types";

interface AssetRecordsListProps {
  assetId: number;
  onRecordAdded: () => void;
  onReminderAdded?: () => void;
  onExpand?: () => void;
  isPublic?: boolean;
  defaultExpanded?: boolean;
  records?: Database['public']['Tables']['asset_records']['Row'][];
}

interface PaginationMetadata {
  currentPage: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasMore: boolean;
}

interface PreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'image' | 'document';
  url: string;
  title?: string;
}

function PreviewModal({ isOpen, onClose, type, url, title }: PreviewModalProps) {
  return (
    <Dialog open={isOpen} onOpenChange={() => onClose()}>
      <DialogContent className="sm:max-w-[800px] p-0">
        <div className="relative">
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-2 top-2 z-10 rounded-full bg-black/20 hover:bg-black/40 text-white"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </Button>
          {type === 'image' ? (
            <img src={url} alt={title || 'Preview'} className="w-full h-auto max-h-[80vh] object-contain" />
          ) : (
            <iframe src={url} title={title || 'Document preview'} className="w-full h-[80vh]" />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RecordCard({ record, onEditClick, isPublic, t, setPreviewState }) {
  const isCreatedToday = record.created_at && isToday(new Date(record.created_at));
  const isCompleted = record.status === "Completed";
  
  // Format dates with relative time
  const formattedCreatedDate = record.created_at ? format(new Date(record.created_at), 'EEEE, MMM d, yyyy \'at\' h:mm a') : '';
  const relativeCreatedDate = record.created_at ? formatDistanceToNow(new Date(record.created_at), { addSuffix: true }) : '';
  const formattedDueDate = record.due_date ? format(new Date(record.due_date), 'MMM d, yyyy') : '';
  const relativeDueDate = record.due_date ? formatDistanceToNow(new Date(record.due_date), { addSuffix: true }) : '';
  const formattedUpdatedDate = record.updated_at ? format(new Date(record.updated_at), 'EEEE, MMM d, yyyy \'at\' h:mm a') : '';
  const relativeUpdatedDate = record.updated_at ? formatDistanceToNow(new Date(record.updated_at), { addSuffix: true }) : '';
  
  // Calculate days remaining to due date
  const daysRemaining = record.due_date ? differenceInDays(new Date(record.due_date), new Date()) : null;
  const isOverdue = daysRemaining !== null && daysRemaining < 0;
  
  return (
    <div className={cn(
      "bg-white rounded-xl border",
      isCompleted ? "border-green-100" : "border-gray-100",
      "shadow-sm hover:shadow-md transition-all duration-200",
      "overflow-hidden relative"
    )}>
      {/* Status indicator */}
      <div className={cn(
        "absolute top-0 left-0 w-1 h-full",
        record.status && getStatusColor(record.status)
      )} />

      {isCreatedToday && (
        <div className="absolute top-2 right-2 h-3 w-3 rounded-full bg-red-500 animate-pulse" />
      )}

      {/* Header Section with clear visual separation */}
      <div className="p-4 border-b border-gray-100 bg-gray-50/30">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1 flex-1">
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-medium text-gray-900 text-base">
                {record.title || t('records.untitledRecord')}
              </h3>
              
              {/* Date information at the top */}
              {record.updated_at && (
                <div className="text-xs text-gray-500 flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  <span>{relativeUpdatedDate}</span>
                </div>
              )}
            </div>
            {record.details && (
              <p className="text-sm text-gray-600 line-clamp-2">
                {record.details}
              </p>
            )}
          </div>
          {!isPublic && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEditClick(record)}
              className="text-gray-400 hover:text-gray-600"
            >
              <Pencil className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Status badges - more prominent placement */}
        <div className="flex flex-wrap gap-2 mt-3">
          {record.type && (
            <Badge className="text-xs bg-gray-100 text-gray-700 font-medium border-0">
              {record.type}
            </Badge>
          )}
          {record.status && (
            <Badge className={cn("text-xs font-medium border-0", getStatusColor(record.status))}>
              {record.status}
            </Badge>
          )}
          {record.priority && (
            <Badge className={cn("text-xs font-medium border-0", getPriorityColor(record.priority))}>
              {record.priority}
            </Badge>
          )}
          {record.progress !== null && record.progress !== undefined && (
            <Badge variant="secondary" className="text-xs bg-blue-50 text-blue-700 font-medium border-0">
              {record.progress}%
            </Badge>
          )}
          {record.category && (
            <Badge variant="outline" className="text-xs bg-white">
              {record.category}
            </Badge>
          )}
        </div>
      </div>

      {/* Content Sections with better information grouping */}
      <div className="p-4 grid gap-4 md:grid-cols-2">
        {/* Left column - Critical information */}
        <div className="space-y-4">
          {/* Dates section - enhanced and moved to top */}
          <div className="grid gap-2">
            <div className="text-xs uppercase tracking-wider text-gray-500 font-medium">Timeline</div>
            <div className="space-y-2">
              {record.updated_at && (
                <div className="bg-gray-50 rounded-md p-2">
                  <div className="flex items-center justify-between gap-1">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-gray-400" />
                      <div>
                        <div className="text-xs text-gray-500">Last Updated</div>
                        <span className="text-sm font-medium">{formattedUpdatedDate}</span>
                      </div>
                    </div>
                    <div className="text-xs font-medium text-gray-500">
                      {relativeUpdatedDate}
                    </div>
                  </div>
                </div>
              )}
              
              {record.created_at && (
                <div className="bg-gray-50 rounded-md p-2">
                  <div className="flex items-center justify-between gap-1">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-gray-400" />
                      <div>
                        <div className="text-xs text-gray-500">Created</div>
                        <span className="text-sm font-medium">{formattedCreatedDate}</span>
                      </div>
                    </div>
                    <div className="text-xs font-medium text-gray-500">
                      {relativeCreatedDate}
                    </div>
                  </div>
                </div>
              )}
              
              {record.due_date && (
                <div className={cn(
                  "rounded-md p-2",
                  isOverdue ? "bg-red-50" : "bg-gray-50"
                )}>
                  <div className="flex items-center justify-between gap-1">
                    <div className="flex items-center gap-2">
                      <Calendar className={cn(
                        "h-4 w-4", 
                        isOverdue ? "text-red-500" : "text-gray-400"
                      )} />
                      <div>
                        <div className={cn(
                          "text-xs",
                          isOverdue ? "text-red-500" : "text-gray-500"
                        )}>Due Date</div>
                        <span className={cn(
                          "text-sm font-medium",
                          isOverdue ? "text-red-700" : "text-gray-700"
                        )}>{formattedDueDate}</span>
                      </div>
                    </div>
                    <div className={cn(
                      "text-xs font-medium",
                      isOverdue ? "text-red-500" : "text-gray-500"
                    )}>
                      {relativeDueDate}
                    </div>
                  </div>
                  {daysRemaining !== null && (
                    <div className={cn(
                      "text-xs mt-1 text-right",
                      isOverdue ? "text-red-500" : "text-gray-500"
                    )}>
                      {isOverdue 
                        ? `Overdue by ${Math.abs(daysRemaining)} days` 
                        : daysRemaining === 0 
                          ? "Due today" 
                          : `${daysRemaining} days remaining`}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Asset Information */}
          {record.assets && (
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
              <div className="text-xs uppercase tracking-wider text-blue-700 font-semibold mb-1">{t('records.assetDetails')}</div>
              <div className="space-y-1">
                <div className="text-sm font-medium text-blue-700">
                  {record.assets.asset_name || record.assets.name}
                </div>
                {(record.assets.company || record.assets.place) && (
                  <div className="text-xs text-blue-600">
                    {record.assets.company?.name}
                    {record.assets.company && record.assets.place && " • "}
                    {record.assets.place?.name}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Cost */}
          {record.cost !== null && (
            <div>
              <div className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-1">Cost</div>
              <div className="bg-gray-50 rounded-md p-2">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">${record.cost}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right column - Supporting information */}
        <div className="space-y-4">
          {/* Contact & Location */}
          {(record.location || record.assigned_to || record.phone) && (
            <div>
              <div className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-1">Contact Information</div>
              <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                {record.assigned_to && (
                  <div className="flex items-center gap-2 text-sm">
                    <User className="h-4 w-4 text-gray-500" />
                    <div>
                      <span className="text-xs text-gray-500">Assigned to</span>
                      <div className="font-medium text-gray-700">{record.assigned_to}</div>
                    </div>
                  </div>
                )}
                {record.location && (
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-gray-500" />
                    <div>
                      <span className="text-xs text-gray-500">Location</span>
                      <div className="font-medium text-gray-700">{record.location}</div>
                    </div>
                  </div>
                )}
                {record.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-gray-500" />
                    <div>
                      <span className="text-xs text-gray-500">Phone</span>
                      <div className="font-medium text-gray-700">{record.phone}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Additional Info */}
          {(record.approval_status || record.alert || record.notes) && (
            <div>
              <div className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-1">Additional Details</div>
              <div className="bg-gray-50 rounded-lg p-3 space-y-3">
                {record.approval_status && (
                  <div className="flex items-center gap-2">
                    <Badge 
                      className={cn("font-medium border-0", getStatusColor(record.approval_status))}
                    >
                      {record.approval_status}
                    </Badge>
                    {record.approval_by && (
                      <span className="text-xs text-gray-500">
                        {t('forms.record.approvedBy')} {record.approval_by}
                      </span>
                    )}
                  </div>
                )}
                {record.alert && (
                  <div className="flex items-center gap-2 text-sm bg-yellow-50 p-2 rounded-md text-yellow-700">
                    <AlertCircle className="h-4 w-4" />
                    <span className="font-medium">{t('records.alertEnabled')}</span>
                  </div>
                )}
                {record.notes && (
                  <div className="text-sm text-gray-600 bg-white p-2 rounded-md border border-gray-100">
                    <span className="font-medium text-gray-700 block text-xs mb-1">{t('records.notes')}:</span>
                    <p className="text-sm">{record.notes}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Attachments - Full width for better visibility */}
        {(record.images?.length > 0 || record.documents?.length > 0) && (
          <div className="col-span-full space-y-3 pt-3 border-t border-gray-100 mt-2">
            <div className="text-xs uppercase tracking-wider text-gray-500 font-medium">Attachments</div>
            <div className="grid gap-4 sm:grid-cols-2">
              {record.images && record.images.length > 0 && (
                <div className="space-y-2 bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-sm text-gray-700 font-medium">
                    <ImageIcon className="h-4 w-4" />
                    <span>{t('records.images')} ({record.images.length})</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {record.images.map((image, index) => (
                      <button
                        key={index}
                        onClick={() => setPreviewState({
                          isOpen: true,
                          type: 'image',
                          url: image,
                          title: `${t('records.image')} ${index + 1}`
                        })}
                        className="w-16 h-16 rounded-lg overflow-hidden border border-white hover:border-blue-400 transition-colors shadow-sm hover:shadow"
                      >
                        <img
                          src={image}
                          alt={`${t('records.preview')} ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {record.documents && record.documents.length > 0 && (
                <div className="space-y-2 bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-sm text-gray-700 font-medium">
                    <FileText className="h-4 w-4" />
                    <span>{t('records.documents')} ({record.documents.length})</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {record.documents.map((doc, index) => (
                      <button
                        key={index}
                        onClick={() => setPreviewState({
                          isOpen: true,
                          type: 'document',
                          url: doc,
                          title: `${t('records.document')} ${index + 1}`
                        })}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-gray-100 hover:border-blue-400 transition-colors text-sm text-gray-700 hover:text-blue-600 shadow-sm hover:shadow"
                      >
                        <FileText className="h-4 w-4" />
                        <span>{t('records.doc')} {index + 1}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function AssetRecordsList({ assetId, onRecordAdded, onReminderAdded, onExpand, isPublic, defaultExpanded, records }: AssetRecordsListProps) {
  const { t } = useTranslation();
  const [showRecordDialog, setShowRecordDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const [previewState, setPreviewState] = useState<{
    isOpen: boolean;
    type: 'image' | 'document';
    url: string;
    title?: string;
  }>({
    isOpen: false,
    type: 'image',
    url: '',
    title: '',
  });

  // Add edit dialog state
  const [editDialogState, setEditDialogState] = useState<{
    isOpen: boolean;
    record: any | null;
  }>({
    isOpen: false,
    record: null,
  });

  // Add records query
  const { data: recordsData, refetch: refetchRecords } = useQuery({
    queryKey: ['assetRecords', assetId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('asset_records')
        .select(`
          *,
          assets:asset_id (
            id,
            asset_name,
            name,
            place:place_id (
              id,
              name
            ),
            company:company_id (
              id,
              name
            )
          )
        `)
        .eq('asset_id', assetId)
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      return data;
    },
  });

  const filteredRecords = useMemo(() => {
    if (!recordsData) return [];
    
    if (!searchQuery.trim() && 
        (!statusFilter || statusFilter === 'all') && 
        (!priorityFilter || priorityFilter === 'all') &&
        (!typeFilter || typeFilter === 'all')) 
      return recordsData;
    
    const query = searchQuery.toLowerCase().trim();
    return recordsData.filter((record) => {
      const matchesSearch = !query || (
        (record.title?.toLowerCase().includes(query)) ||
        (record.details?.toLowerCase().includes(query)) ||
        (record.location?.toLowerCase().includes(query)) ||
        (record.assigned_to?.toLowerCase().includes(query)) ||
        (record.type?.toLowerCase().includes(query)) ||
        (record.status?.toLowerCase().includes(query)) ||
        (record.category?.toLowerCase().includes(query))
      );

      const matchesStatus = statusFilter === 'all' || record.status === statusFilter;
      const matchesPriority = priorityFilter === 'all' || record.priority === priorityFilter;
      const matchesType = typeFilter === 'all' || record.type === typeFilter;

      return matchesSearch && matchesStatus && matchesPriority && matchesType;
    });
  }, [recordsData, searchQuery, statusFilter, priorityFilter, typeFilter]);

  const paginationMetadata = useMemo<PaginationMetadata>(() => {
    const totalCount = filteredRecords.length;
    const totalPages = Math.ceil(totalCount / pageSize);
    const from = (currentPage - 1) * pageSize;

    return {
      currentPage,
      pageSize,
      totalCount,
      totalPages,
      hasMore: from + pageSize < totalCount
    };
  }, [filteredRecords, currentPage, pageSize]);

  const paginatedRecords = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredRecords.slice(startIndex, startIndex + pageSize);
  }, [filteredRecords, currentPage, pageSize]);

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    if (paginationMetadata.hasMore) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePreviewClose = () => {
    setPreviewState(prev => ({ ...prev, isOpen: false }));
  };

  const handleRecordSuccess = () => {
    setShowRecordDialog(false);
    refetchRecords();
    onRecordAdded();
  };

  const handleReminderSuccess = () => {
    refetchRecords();
    onReminderAdded?.();
  };

  const handleEditClick = (record: any) => {
    setEditDialogState({
      isOpen: true,
      record,
    });
  };

  const handleEditSuccess = () => {
    setEditDialogState({
      isOpen: false,
      record: null,
    });
    // Refresh records and notify parent
    refetchRecords();
    onRecordAdded?.();
  };

  const handleEditCancel = () => {
    setEditDialogState({
      isOpen: false,
      record: null,
    });
  };

  const renderRecords = () => {
    return (
      <div className="space-y-4">
        {!isPublic && (
          <div className="mb-6">
            <AppleButton
              variant="secondary"
              size="sm"
              onClick={() => setShowRecordDialog(true)}
              className="w-full flex items-center justify-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t('records.new')}
            </AppleButton>
          </div>
        )}

        {/* Search and Filters */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative w-full sm:flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder={t('records.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white border-gray-200 w-full"
              />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 sm:flex gap-2">
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="sm:w-[140px] bg-white border-gray-200">
                  <SelectValue placeholder={t('forms.record.type')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('forms.record.placeholders.selectType')}</SelectItem>
                  {/* Maintenance Types */}
                  <SelectItem value="Preventive Maintenance">{t('forms.record.types.maintenance.preventive')}</SelectItem>
                  <SelectItem value="Corrective Maintenance">{t('forms.record.types.maintenance.corrective')}</SelectItem>
                  <SelectItem value="Emergency Maintenance">{t('forms.record.types.maintenance.emergency')}</SelectItem>
                  <SelectItem value="Scheduled Maintenance">{t('forms.record.types.maintenance.scheduled')}</SelectItem>
                  <SelectItem value="Condition Maintenance">{t('forms.record.types.maintenance.condition')}</SelectItem>
                  {/* Inspection Types */}
                  <SelectItem value="Safety Inspection">{t('forms.record.types.inspection.safety')}</SelectItem>
                  <SelectItem value="Quality Inspection">{t('forms.record.types.inspection.quality')}</SelectItem>
                  <SelectItem value="Compliance Inspection">{t('forms.record.types.inspection.compliance')}</SelectItem>
                  <SelectItem value="Performance Inspection">{t('forms.record.types.inspection.performance')}</SelectItem>
                  {/* Operation Types */}
                  <SelectItem value="Operation Check">{t('forms.record.types.operation.check')}</SelectItem>
                  <SelectItem value="Calibration">{t('forms.record.types.operation.calibration')}</SelectItem>
                  <SelectItem value="Testing">{t('forms.record.types.operation.testing')}</SelectItem>
                  <SelectItem value="Training">{t('forms.record.types.operation.training')}</SelectItem>
                  {/* Incident Types */}
                  <SelectItem value="Breakdown">{t('forms.record.types.incident.breakdown')}</SelectItem>
                  <SelectItem value="Malfunction">{t('forms.record.types.incident.malfunction')}</SelectItem>
                  <SelectItem value="Damage Report">{t('forms.record.types.incident.damageReport')}</SelectItem>
                  <SelectItem value="Safety Incident">{t('forms.record.types.incident.safetyIncident')}</SelectItem>
                  {/* Usage Types */}
                  <SelectItem value="Regular Usage">{t('forms.record.types.usage.regular')}</SelectItem>
                  <SelectItem value="Special Usage">{t('forms.record.types.usage.special')}</SelectItem>
                  <SelectItem value="Temporary Usage">{t('forms.record.types.usage.temporary')}</SelectItem>
                  <SelectItem value="Rental Usage">{t('forms.record.types.usage.rental')}</SelectItem>
                  {/* Service Types */}
                  <SelectItem value="Repair">{t('forms.record.types.service.repair')}</SelectItem>
                  <SelectItem value="Upgrade">{t('forms.record.types.service.upgrade')}</SelectItem>
                  <SelectItem value="Replacement">{t('forms.record.types.service.replacement')}</SelectItem>
                  <SelectItem value="Installation">{t('forms.record.types.service.installation')}</SelectItem>
                  <SelectItem value="Configuration">{t('forms.record.types.service.configuration')}</SelectItem>
                  {/* Documentation Types */}
                  <SelectItem value="Certification">{t('forms.record.types.documentation.certification')}</SelectItem>
                  <SelectItem value="Warranty Claim">{t('forms.record.types.documentation.warrantyClaim')}</SelectItem>
                  <SelectItem value="Insurance Claim">{t('forms.record.types.documentation.insuranceClaim')}</SelectItem>
                  <SelectItem value="Documentation Update">{t('forms.record.types.documentation.documentationUpdate')}</SelectItem>
                  {/* Other */}
                  <SelectItem value="Other">{t('forms.record.types.other')}</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className={cn(
                  "sm:w-[140px] bg-white border-gray-200",
                  statusFilter && getStatusColor(statusFilter)
                )}>
                  <SelectValue placeholder={t('records.status')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="Pending" className={getStatusColor('Pending')}>Pending</SelectItem>
                  <SelectItem value="In Progress" className={getStatusColor('In Progress')}>In Progress</SelectItem>
                  <SelectItem value="Completed" className={getStatusColor('Completed')}>Completed</SelectItem>
                  <SelectItem value="Cancelled" className={getStatusColor('Cancelled')}>Cancelled</SelectItem>
                </SelectContent>
              </Select>
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className={cn(
                  "sm:w-[140px] bg-white border-gray-200",
                  priorityFilter && getPriorityColor(priorityFilter)
                )}>
                  <SelectValue placeholder={t('records.priority')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priority</SelectItem>
                  <SelectItem value="Low" className={getPriorityColor('Low')}>Low</SelectItem>
                  <SelectItem value="Medium" className={getPriorityColor('Medium')}>Medium</SelectItem>
                  <SelectItem value="High" className={getPriorityColor('High')}>High</SelectItem>
                  <SelectItem value="Critical" className={getPriorityColor('Critical')}>Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        
        {/* Records List */}
        {filteredRecords.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-sm text-gray-500">
              {searchQuery || statusFilter !== 'all' || priorityFilter !== 'all' || typeFilter !== 'all'
                ? t('records.noSearchResults') 
                : t('records.noRecords')}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {paginatedRecords.map((record) => (
              <RecordCard
                key={record.id}
                record={record}
                onEditClick={handleEditClick}
                isPublic={isPublic}
                t={t}
                setPreviewState={setPreviewState}
              />
            ))}

            {/* Pagination */}
            {paginationMetadata && (
              <div className="mt-6 flex items-center justify-between border-t border-gray-100 pt-4">
                <div className="text-sm text-gray-500">
                  {t('records.showing')} {((paginationMetadata.currentPage - 1) * paginationMetadata.pageSize) + 1} {t('records.to')}{' '}
                  {Math.min(paginationMetadata.currentPage * paginationMetadata.pageSize, paginationMetadata.totalCount)} {t('records.of')}{' '}
                  {paginationMetadata.totalCount} {t('records.records')}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    {t('records.previous')}
                  </Button>
                  <div className="text-sm text-gray-500 px-4">
                    {t('records.page')} {paginationMetadata.currentPage} {t('records.of')} {paginationMetadata.totalPages}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleNextPage}
                    disabled={!paginationMetadata.hasMore}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    {t('records.next')}
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Accordion 
        type="single" 
        collapsible 
        defaultValue={defaultExpanded ? "items" : undefined}
        className="w-full rounded-xl bg-white border border-gray-100"
      >
        <AccordionItem value="items" className="border-none">
          <AccordionTrigger 
            className="text-sm px-4 py-3 hover:no-underline bg-blue-50 hover:bg-blue-100 transition-colors shadow-md"
            onClick={() => onExpand?.()}
          >
            <div className="flex items-center">
              <span className="font-medium">{t('asset.items')} ({recordsData?.length || 0})</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 py-4">
            <Tabs defaultValue="records" className="w-full">
              <TabsList className="w-full mb-6 bg-gray-50 p-1 rounded-lg">
                <TabsTrigger value="records" className="flex-1 data-[state=active]:bg-white">
                  <FileText className="h-4 w-4 mr-2" />
                  {t('records.title')} ({recordsData?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="reminders" className="flex-1 data-[state=active]:bg-white">
                  <Bell className="h-4 w-4 mr-2" />
                  {t('reminders.title')}
                </TabsTrigger>
              </TabsList>
              <TabsContent value="records">
                {renderRecords()}
              </TabsContent>
              <TabsContent value="reminders">
                <AssetReminders
                  assetId={assetId}
                  onReminderAdded={handleReminderSuccess}
                  isPublic={isPublic}
                />
              </TabsContent>
            </Tabs>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* Preview Modal */}
      <PreviewModal {...previewState} onClose={handlePreviewClose} />

      {/* Add Record Dialog */}
      <Dialog open={showRecordDialog} onOpenChange={setShowRecordDialog}>
        <DialogContent className="sm:max-w-[600px] h-[90vh] flex flex-col">
          <div className="flex flex-col h-full">
            <DialogHeader>
              <DialogTitle>{t('records.createRecord')}</DialogTitle>
              <DialogDescription>
                {t('records.createRecordDescription')}
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto">
              <AssetRecordForm
                assetId={assetId}
                mode="create"
                onSuccess={handleRecordSuccess}
                onCancel={() => setShowRecordDialog(false)}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Record Dialog */}
      <Dialog open={editDialogState.isOpen} onOpenChange={(open) => !open && handleEditCancel()}>
        <DialogContent className="sm:max-w-[600px] h-[90vh] flex flex-col">
          <div className="flex flex-col h-full">
            <DialogHeader>
              <DialogTitle>{t('records.editRecord')}</DialogTitle>
              <DialogDescription>
                {t('records.editRecordDescription')}
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto">
              <AssetRecordForm
                assetId={assetId}
                initialData={editDialogState.record}
                mode="edit"
                onSuccess={handleEditSuccess}
                onCancel={handleEditCancel}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}